import React, { useCallback, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';

import { AnalyzeQuotaModal } from '../../components/billing/AnalyzeQuotaModal';
import { formatCurrency } from '../../components/charts/d3/format';
import { GlassButton } from '../../components/ui/GlassButton';
import { APP_NAME } from '../../config/branding';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useImportOnboarding } from '../../contexts/ImportOnboardingContext';
import { useAuth } from '../../contexts/AuthContext';
import { useBillingActions } from '../../hooks/useBillingActions';
import { forcePullDashboards } from '../../services/analyticsService';
import { ImportDropZone } from './components/ImportDropZone';
import { ImportImageList } from './components/ImportImageList';
import { ImportProgressPanel } from './components/ImportProgressPanel';
import { ImportTransactionList } from './components/ImportTransactionList';
import { pickFromDocuments, pickFromGallery } from './pickers/pickImportImages';
import { isPartialImport } from './utils/importDerivedState';

type Props = {
  maxHeight: number;
};

export function ImportOnboardingModalContent({ maxHeight }: Props) {
  const {
    close,
    minimize,
    images,
    progress,
    busy,
    categoryBreakdown,
    monthSections,
    remainingAnalyzes,
    analyzeLimit,
    quotaModal,
    dismissQuotaModal,
    addImages,
    removeImage,
    beginImport,
    skipOnboarding,
    reset,
  } = useImportOnboarding();
  const { user } = useAuth();
  const { upgradeToPro, busy: upgradeBusy, error: billingError } = useBillingActions();
  const [pickerError, setPickerError] = useState<string | null>(null);

  const handleUpgrade = useCallback(async () => {
    try {
      await upgradeToPro('analyze_quota');
      dismissQuotaModal();
    } catch {
      // Error surfaced via billingError / modal stays open.
    }
  }, [dismissQuotaModal, upgradeToPro]);

  const uploadCtaLabel = (() => {
    if (busy) return 'Working…';
    if (user?.plan === 'pro') return 'Upload & analyze · Pro';
    if (typeof remainingAnalyzes === 'number' && typeof analyzeLimit === 'number') {
      return `Upload & analyze · ${remainingAnalyzes} of ${analyzeLimit} left`;
    }
    return 'Upload & analyze';
  })();

  const handlePickGallery = useCallback(async () => {
    setPickerError(null);
    try {
      const next = await pickFromGallery();
      addImages(next);
    } catch (error) {
      setPickerError(error instanceof Error ? error.message : 'Could not open gallery');
    }
  }, [addImages]);

  const handlePickDocuments = useCallback(async () => {
    setPickerError(null);
    try {
      const next = await pickFromDocuments();
      addImages(next);
    } catch (error) {
      setPickerError(error instanceof Error ? error.message : 'Could not open files');
    }
  }, [addImages]);

  const handleSkip = useCallback(async () => {
    try {
      await skipOnboarding();
      close();
    } catch (error) {
      setPickerError(error instanceof Error ? error.message : 'Could not skip');
    }
  }, [close, skipOnboarding]);

  const handleDone = useCallback(() => {
    void forcePullDashboards({
      userId: user?.id,
      reason: 'import_modal_done',
    });
    reset();
    close();
  }, [close, reset, user?.id]);

  const handleRetry = useCallback(() => {
    reset();
  }, [reset]);

  const handleDismiss = useCallback(() => {
    if (busy) minimize();
    else close();
  }, [busy, close, minimize]);

  const isComplete = progress.phase === 'complete';
  const isFailed = progress.phase === 'failed';
  const isPartial = isComplete && isPartialImport(progress);
  const showPickers = !busy && !isComplete;
  const showLiveList =
    (busy || isComplete) &&
    (progress.transactions.length > 0 || progress.phase === 'processing');
  const topCategories = categoryBreakdown.slice(0, 6);
  const categoryMaxAmount = Math.max(...topCategories.map((c) => c.amount), 1);

  return (
    <View style={[styles.root, { maxHeight }]}>
      <AnalyzeQuotaModal
        visible={quotaModal != null}
        info={
          quotaModal && billingError
            ? { ...quotaModal, message: `${quotaModal.message}\n\n${billingError}` }
            : quotaModal
        }
        onDismiss={dismissQuotaModal}
        onUpgrade={handleUpgrade}
        upgradeBusy={upgradeBusy}
        source="analyze_quota"
      />
      <View style={styles.header}>
        <View style={styles.headerTop}>
          <Text style={styles.kicker}>{APP_NAME}</Text>
          <GlassButton
            variant="ghost"
            size="sm"
            onPress={handleDismiss}
            accessibilityLabel={
              busy ? 'Hide import sheet — import continues in background' : 'Close'
            }
          >
            {busy ? 'Hide' : 'Close'}
          </GlassButton>
        </View>
        <Text style={styles.title}>
          {isPartial
            ? 'Some statements failed'
            : isComplete
              ? 'You’re set'
              : 'Import statements & screenshots'}
        </Text>
        <Text style={styles.subtitle}>
          {isPartial
            ? 'The rest imported. Re-upload the failed PDF so those purchases aren’t missing.'
            : isComplete
            ? progress.transactions.length > 0
              ? 'Review spend below (newest first). Investment balances from brokerage screenshots are added to Wealth.'
              : 'Spend and investment balances update from what we could read. Import more anytime.'
            : busy
              ? 'You can hide this sheet — import keeps running in the background.'
              : 'Bank/card statements fill spend. Robinhood, Wealthsimple, or brokerage screenshots fill your portfolio — same upload.'}
        </Text>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {showPickers ? (
          <View style={styles.pickRow}>
            <GlassButton
              variant="secondary"
              size="sm"
              onPress={() => {
                void handlePickGallery();
              }}
              disabled={busy}
              style={styles.pickBtn}
            >
              {Platform.OS === 'web' ? 'Choose files' : 'Photo library'}
            </GlassButton>
            {Platform.OS !== 'web' ? (
              <GlassButton
                variant="secondary"
                size="sm"
                onPress={() => {
                  void handlePickDocuments();
                }}
                disabled={busy}
                style={styles.pickBtn}
              >
                Files / PDF
              </GlassButton>
            ) : null}
          </View>
        ) : null}

        {showPickers ? (
          <ImportDropZone
            disabled={busy}
            onFiles={addImages}
            onError={setPickerError}
          />
        ) : null}

        <ImportImageList images={images} onRemove={removeImage} disabled={busy} />

        <ImportProgressPanel progress={progress} />

        {topCategories.length > 0 && (busy || isComplete) ? (
          <View style={styles.categoryStrip} accessibilityLabel="Category breakdown">
            {topCategories.map((item) => (
              <View key={item.categoryName} style={styles.categoryChip}>
                <Text style={styles.categoryName} numberOfLines={1}>
                  {item.categoryName}
                </Text>
                <View style={styles.categoryBarTrack}>
                  <View
                    style={[
                      styles.categoryBarFill,
                      {
                        width: `${Math.max(
                          8,
                          Math.round((item.amount / categoryMaxAmount) * 100),
                        )}%`,
                      },
                    ]}
                  />
                </View>
                <Text style={styles.categoryMeta}>
                  {formatCurrency(item.amount)} · {item.count}
                </Text>
              </View>
            ))}
          </View>
        ) : null}

        {showLiveList ? (
          <ImportTransactionList
            monthSections={monthSections}
            transactionCount={progress.transactions.length}
            loading={busy && progress.transactions.length === 0}
          />
        ) : null}

        {(pickerError || progress.error) && progress.phase === 'idle' ? (
          <Text style={styles.error}>{pickerError || progress.error}</Text>
        ) : null}
      </ScrollView>

      <View style={styles.footer}>
        {isComplete ? (
          <GlassButton variant="primary" fullWidth onPress={handleDone}>
            Continue to Home
          </GlassButton>
        ) : isFailed ? (
          <>
            <GlassButton
              variant="primary"
              fullWidth
              onPress={() => {
                void beginImport();
              }}
            >
              Try again
            </GlassButton>
            <GlassButton variant="ghost" fullWidth onPress={handleRetry}>
              Clear & start over
            </GlassButton>
          </>
        ) : (
          <>
            <GlassButton
              variant="primary"
              fullWidth
              disabled={busy || images.length === 0}
              onPress={() => {
                void beginImport();
              }}
            >
              {uploadCtaLabel}
            </GlassButton>
            <GlassButton variant="ghost" fullWidth disabled={busy} onPress={() => void handleSkip()}>
              I’ll do this later
            </GlassButton>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 0,
  },
  header: {
    gap: spacing.xs,
    marginBottom: spacing.md,
    flexShrink: 0,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  kicker: {
    ...typography.label,
    color: colors.accent,
    textTransform: 'uppercase',
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  subtitle: {
    ...typography.body,
    color: colors.textMuted,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    gap: spacing.md,
    paddingBottom: spacing.md,
  },
  pickRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  pickBtn: {
    flexGrow: 1,
  },
  categoryStrip: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  categoryChip: {
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
    maxWidth: '48%',
    flexGrow: 1,
    gap: 4,
  },
  categoryName: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  categoryBarTrack: {
    height: 3,
    borderRadius: radii.pill,
    backgroundColor: 'rgba(255,255,255,0.08)',
    overflow: 'hidden',
  },
  categoryBarFill: {
    height: '100%',
    borderRadius: radii.pill,
    backgroundColor: colors.accent,
  },
  categoryMeta: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
    fontSize: 11,
  },
  error: {
    ...typography.caption,
    color: colors.danger,
  },
  footer: {
    gap: spacing.sm,
    paddingTop: spacing.sm,
    flexShrink: 0,
  },
});
